/* eslint-disable ember/no-classic-components, ember/require-tagless-components */
import Component from "@ember/component";
import { action, computed } from "@ember/object";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import { service } from "@ember/service";
import { isBlank } from "@ember/utils";
import { classNames } from "@ember-decorators/component";
import noop from "discourse/helpers/noop";
import { bind } from "discourse/lib/decorators";
import {
  authorizedExtensions,
  authorizesAllExtensions,
} from "discourse/lib/uploads";
import DButton from "discourse/ui-kit/d-button";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import { i18n } from "discourse-i18n";

// This picker is intended to be used with UppyUploadMixin or with
// ComposerUploadUppy, which is why there are no change events registered
// for the input. They are handled by the uppy mixins directly.
//
// However, if you provide an onFilesPicked action to this component, the change
// binding will still be added, and the file type will be validated here. This
// is sometimes useful if you need to do something outside the uppy upload with
// the file, such as directly using JSON or CSV data from a file in JS.
@classNames("pick-files-button")
export default class DPickFilesButton extends Component {
  @service dialog;

  /** @type {string | null | undefined} */
  fileInputId = null;

  /** @type {string | null | undefined} */
  fileInputClass = null;
  fileInputDisabled = false;

  /** @type {string | string[] | null | undefined} */
  acceptedFormatsOverride = null;
  allowMultiple = false;
  showButton = false;

  @computed()
  get acceptsAllFormats() {
    return (
      this.capabilities.isIOS ||
      authorizesAllExtensions(this.currentUser.staff, this.siteSettings)
    );
  }

  @computed()
  get acceptedFormats() {
    // the acceptedFormatsOverride can be a list of extensions or mime types
    if (!isBlank(this.acceptedFormatsOverride)) {
      return this.acceptedFormatsOverride;
    }

    const extensions = authorizedExtensions(
      this.currentUser.staff,
      this.siteSettings
    );

    return extensions.map((ext) => `.${ext}`).join();
  }

  didInsertElement() {
    super.didInsertElement(...arguments);

    if (this.onFilesPicked) {
      const fileInput = this.element.querySelector("input");
      this.set("fileInput", fileInput);
      fileInput.addEventListener("change", this.onChange, false);
    }
  }

  willDestroyElement() {
    super.willDestroyElement(...arguments);

    if (this.onFilesPicked) {
      this.fileInput.removeEventListener("change", this.onChange);
    }
  }

  @bind
  onChange() {
    const files = this.fileInput.files;
    this._filesPicked(files);
  }

  @action
  openSystemFilePicker() {
    this.fileInput.click();
  }

  _filesPicked(files) {
    if (!files || !files.length) {
      return;
    }

    if (!this._haveAcceptedTypes(files)) {
      const message = i18n("pick_files_button.unsupported_file_picked", {
        types: this.acceptedFileTypesString,
      });
      this.dialog.alert(message);
      return;
    }

    if (typeof this.onFilesPicked === "function") {
      this.onFilesPicked(files);
    }
  }

  _haveAcceptedTypes(files) {
    for (const file of files) {
      if (!this._hasAcceptedExtensionOrType(file)) {
        return false;
      }
    }
    return true;
  }

  _hasAcceptedExtensionOrType(file) {
    const extension = this._fileExtension(file.name);
    return (
      this.acceptedFormats.includes(`.${extension}`) ||
      this.acceptedFormats.includes(file.type)
    );
  }

  _fileExtension(fileName) {
    return fileName.split(".").pop();
  }

  <template>
    {{#if this.showButton}}
      <DButton
        class={{dConcatClass this.class "btn-default"}}
        @action={{this.openSystemFilePicker}}
        @icon={{this.icon}}
        @label={{this.label}}
      />
    {{/if}}
    {{#if this.acceptsAllFormats}}
      <input
        class={{this.fileInputClass}}
        disabled={{this.fileInputDisabled}}
        id={{this.fileInputId}}
        multiple={{this.allowMultiple}}
        type="file"
        {{didInsert (or @registerFileInput (noop))}}
      />
    {{else}}
      <input
        accept={{this.acceptedFormats}}
        class={{this.fileInputClass}}
        disabled={{this.fileInputDisabled}}
        id={{this.fileInputId}}
        multiple={{this.allowMultiple}}
        type="file"
        {{didInsert (or @registerFileInput (noop))}}
      />
    {{/if}}
  </template>
}
